import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const usps = [
  { icon: 'ri-money-euro-circle-line', title: 'Bespaar tot 30–80% op inkoop', desc: 'Direct bij de fabriek = geen tussenlagen, geen opslag. De laagste prijs.' },
  { icon: 'ri-shield-check-line', title: 'Koop Direct in zonder gedoe', desc: 'Wij zoeken de fabrieken, wij regelen de onderhandelingen, wij doen de kwaliteitschecks en wij zorgen voor de levering!' },
  { icon: 'ri-rocket-line', title: 'Snellere schaalbaarheid', desc: 'Groei je business zonder inkoopkosten die meegroeien. Meer marge per product.' },
  { icon: 'ri-paint-brush-line', title: 'Volledig maatwerk mogelijk', desc: 'Eigen branding, logo, maten, kleuren. Jouw product, jouw identiteit.' },
  { icon: 'ri-percent-line', title: 'Betere marges voor webshops', desc: 'Koop in voor fracties van NL-prijzen. Verdien meer op elk product.' },
  { icon: 'ri-eye-line', title: 'Geen verborgen kosten', desc: 'Transparante prijzen, all-in offertes. Wat je ziet is wat je betaalt.' },
];

const ddpPunten = [
  { icon: 'ri-truck-line', text: 'Transport geregeld' },
  { icon: 'ri-file-text-line', text: 'Invoerrechten inbegrepen' },
  { icon: 'ri-bank-line', text: 'BTW inbegrepen' },
];

const trustBadges = [
  { icon: 'ri-global-line', text: 'Internationale sourcing ervaring' },
  { icon: 'ri-building-4-line', text: 'Samenwerking met meerdere fabrieken' },
  { icon: 'ri-ship-line', text: 'Kennis van import & logistiek' },
  { icon: 'ri-customer-service-2-line', text: 'Transparante communicatie' },
];

export default function USPSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="py-12 md:py-20 bg-[#F7F8FA] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-10">
        <div className="text-center mb-8 md:mb-12">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-2 text-[#3B9EE8] text-sm font-600 mb-6"
          >
            <i className="ri-star-line" />
            Waarom DUC
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-3"
          >
            Waarom klanten kiezen
            <br />
            <span className="text-[#3B9EE8]">voor DUC</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
          {usps.map((usp, i) => (
            <motion.div
              key={usp.title}
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.09 }}
              className="bg-white rounded-xl p-6 border border-[#0A1628]/6 hover:border-[#3B9EE8]/30 transition-all duration-300 group cursor-default"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#3B9EE8]/10 mb-4 group-hover:bg-[#3B9EE8]/20 transition-colors">
                <i className={`${usp.icon} text-[#3B9EE8] text-xl`} />
              </div>
              <h3 className="font-700 text-[#0A1628] text-base mb-2">{usp.title}</h3>
              <p className="text-[#0A1628]/55 text-sm leading-relaxed">{usp.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-[#0A1628] rounded-2xl p-8 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#3B9EE8]/5 rounded-full blur-2xl pointer-events-none" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 border border-[#3B9EE8]/30 bg-[#3B9EE8]/10 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-truck-line text-[#3B9EE8] text-sm" />
                <span className="text-[#3B9EE8] text-sm font-600">DDP Levering</span>
              </div>
              <h3 className="font-heading font-700 text-white text-2xl mb-2">Levering via DDP</h3>
              <p className="text-white/55 text-sm mb-6 leading-relaxed">
                Geen verrassingen achteraf. Je betaalt één prijs — alles zit erin.
              </p>
              <div className="flex flex-col gap-4">
                {ddpPunten.map((punt) => (
                  <div key={punt.text} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3B9EE8]/15 flex-shrink-0">
                      <i className={`${punt.icon} text-[#3B9EE8] text-sm`} />
                    </div>
                    <span className="text-white/80 text-sm font-500">&#10003; {punt.text}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/8">
                <p className="text-white/40 text-xs">
                  DDP = Delivered Duty Paid. De meest complete leveringsconditie — je product arriveert klaar voor gebruik.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="bg-white rounded-2xl p-8 border border-[#0A1628]/6"
          >
            <div className="inline-flex items-center gap-2 border border-[#3B9EE8]/40 bg-[#3B9EE8]/10 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-verified-badge-line text-[#3B9EE8] text-sm" />
              <span className="text-[#3B9EE8] text-sm font-600">Waarom DUC betrouwbaar is</span>
            </div>
            <h3 className="font-heading font-700 text-[#0A1628] text-2xl mb-5">Jij bespaart zonder risico</h3>
            <div className="space-y-4 mb-6">
              {trustBadges.map((badge) => (
                <div key={badge.text} className="flex items-center gap-4 p-3 rounded-xl bg-[#F7F8FA] hover:bg-[#3B9EE8]/8 transition-colors cursor-default">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#0A1628]/8 flex-shrink-0">
                    <i className={`${badge.icon} text-[#3B9EE8] text-base`} />
                  </div>
                  <span className="text-[#0A1628]/75 text-sm font-500">{badge.text}</span>
                  <i className="ri-check-double-line text-[#3B9EE8] ml-auto text-sm" />
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('#contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm py-3.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Ontvang binnen 24 uur een prijsindicatie
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
